// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { formatHumanName } from '@medplum/core';
import type { Extension, HealthcareService, Practitioner, Schedule } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM } from '@polarissleep/scheduling-next';
import { useEffect, useState } from 'react';
import { getProviderAppEnvConfig } from '../config/env';
import { POLARIS_ENV_TAG_SYSTEM } from '../scheduling-next/constants';

export interface ActivePractitionerOption {
  value: string;
  label: string;
  practitioner: Practitioner;
}

export function useActivePractitioners(): {
  practitioners: ActivePractitionerOption[];
  loading: boolean;
  error: Error | null;
} {
  const medplum = useMedplum();
  const { medplumOrganizationId, polarisEnv, schedulingEngine } = getProviderAppEnvConfig();
  const [practitioners, setPractitioners] = useState<ActivePractitionerOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;
    const search: Record<string, string> = { _count: '300' };
    if (medplumOrganizationId) {
      search._compartment = `Organization/${medplumOrganizationId}`;
    }
    if (polarisEnv) {
      search._tag = `${POLARIS_ENV_TAG_SYSTEM}|${polarisEnv}`;
    }

    medplum
      .searchResources('Practitioner', search, { cache: 'no-cache' })
      .then(async (results) => {
        // Legacy practitioners predate environment tagging. Keep tenant
        // compartment scoping, but allow those records to remain selectable.
        if (results.length === 0 && schedulingEngine === 'legacy' && medplumOrganizationId && polarisEnv) {
          return medplum.searchResources(
            'Practitioner',
            { _count: '300', _compartment: `Organization/${medplumOrganizationId}` },
            { cache: 'no-cache' }
          );
        }
        return results;
      })
      .then((results) => {
        return schedulingEngine === 'medplum-v2'
          ? filterToV2SchedulablePractitioners(medplum, results, medplumOrganizationId, polarisEnv)
          : results;
      })
      .then((results) => {
        if (!mounted) {
          return;
        }

        const next = results
          .filter((practitioner) => practitioner.id && practitioner.active !== false)
          .map((practitioner) => ({
            value: practitioner.id as string,
            label: practitioner.name?.[0] ? formatHumanName(practitioner.name[0]) : 'Unnamed practitioner',
            practitioner,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));

        setPractitioners(next);
        setError(null);
      })
      .catch((err) => {
        if (!mounted) {
          return;
        }
        setError(err instanceof Error ? err : new Error('Failed to load practitioners'));
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, [medplum, medplumOrganizationId, polarisEnv, schedulingEngine]);

  return { practitioners, loading, error };
}

async function filterToV2SchedulablePractitioners(
  medplum: ReturnType<typeof useMedplum>,
  practitioners: Practitioner[],
  organizationId: string | undefined,
  environment: string | undefined
): Promise<Practitioner[]> {
  if (!organizationId || !environment || practitioners.length === 0) {
    return [];
  }

  const [services, schedules] = await Promise.all([
    medplum.searchResources(
      'HealthcareService',
      {
        active: 'true',
        _compartment: `Organization/${organizationId}`,
        _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
        _count: '1000',
      },
      { cache: 'no-cache' }
    ),
    medplum.searchResources(
      'Schedule',
      {
        active: 'true',
        _compartment: `Organization/${organizationId}`,
        _tag: `${POLARIS_ENV_TAG_SYSTEM}|${environment}`,
        _count: '1000',
      },
      { cache: 'no-cache' }
    ),
  ]);
  const projectedServiceReferences = new Set(
    services
      .filter(
        (service: HealthcareService) =>
          hasSingleEnvironmentTag(service, environment) &&
          service.id &&
          service.identifier?.some(
            (identifier) => identifier.system === APPOINTMENT_TYPE_HEALTHCARE_SERVICE_IDENTIFIER_SYSTEM
          )
      )
      .map((service) => `HealthcareService/${service.id}`)
  );
  const schedulablePractitionerIds = new Set(
    schedules.flatMap((schedule: Schedule) => {
      const practitionerReference = schedule.actor?.find((actor: NonNullable<Schedule['actor']>[number]) =>
        actor.reference?.startsWith('Practitioner/')
      )?.reference;
      const hasProjectedService = (schedule.serviceType ?? []).some(
        (serviceType: NonNullable<Schedule['serviceType']>[number]) =>
          serviceType.extension?.some(
            (extension: Extension) =>
              extension.url === 'https://medplum.com/fhir/service-type-reference' &&
              extension.valueReference?.reference &&
              projectedServiceReferences.has(extension.valueReference.reference)
          )
      );
      return practitionerReference && hasProjectedService ? [practitionerReference.replace('Practitioner/', '')] : [];
    })
  );

  return practitioners.filter((practitioner) => practitioner.id && schedulablePractitionerIds.has(practitioner.id));
}

function hasSingleEnvironmentTag(
  resource: { meta?: { tag?: Array<{ system?: string; code?: string }> } },
  environment: string
): boolean {
  const tags = (resource.meta?.tag ?? []).filter((tag) => tag.system === POLARIS_ENV_TAG_SYSTEM);
  return tags.length === 1 && tags[0].code === environment;
}
